import React from "react";

import {
  persistDataManagerTableScrollState,
  readDataManagerTableScrollState,
} from "../utils/dataManagerTableScrollStorage.js";

const SCROLL_PERSIST_DEBOUNCE_MS = 120;

function applyScrollPosition(container, scrollState) {
  if (!container || !scrollState) {
    return;
  }

  container.scrollLeft = scrollState.scrollLeft;
  container.scrollTop = scrollState.scrollTop;
}

export default function useDataManagerTableScrollRestore(tableKey, restoreDependency = 0) {
  const scrollContainerRef = React.useRef(null);
  const persistTimeoutRef = React.useRef(null);

  React.useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return undefined;
    }

    const savedScrollState = readDataManagerTableScrollState(tableKey);
    if (!savedScrollState) {
      return undefined;
    }

    applyScrollPosition(container, savedScrollState);
    const frameId = window.requestAnimationFrame(() => {
      applyScrollPosition(container, savedScrollState);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [restoreDependency, tableKey]);

  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return undefined;
    }

    const handleScroll = () => {
      if (persistTimeoutRef.current) {
        window.clearTimeout(persistTimeoutRef.current);
      }

      persistTimeoutRef.current = window.setTimeout(() => {
        persistDataManagerTableScrollState(tableKey, {
          scrollLeft: container.scrollLeft,
          scrollTop: container.scrollTop,
        });
      }, SCROLL_PERSIST_DEBOUNCE_MS);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (persistTimeoutRef.current) {
        window.clearTimeout(persistTimeoutRef.current);
      }
    };
  }, [restoreDependency, tableKey]);

  return scrollContainerRef;
}
